import React from 'react';
import './SubscriptionCatalogPanel.css';
import { scrollElementWithinConsole } from '../consoleScroll';

type ProjectAccess = {
  project_id: string;
  project_name?: string;
  role: string;
  source?: string;
  group_company_id?: number | null;
  group_name?: string | null;
};

type TeamMember = {
  user_id: string;
  email: string;
  job: string;
  invite_status?: string | null;
  invite_created_at?: string | null;
  invite_last_sent_at?: string | null;
  invite_accepted_at?: string | null;
  invite_expires_at?: string | null;
  last_login_at?: string | null;
  membership_source?: string | null;
  linked_groups?: Array<{ company_id: number; name: string; group_path?: string }>;
  project_access?: ProjectAccess[];
  roles?: string[];
};

type ProjectOption = {
  project_id: string;
  project_name: string;
};

type TeamSubscription = {
  company_id: number;
  company_name: string;
  members: TeamMember[];
  projects: ProjectOption[];
  licenses_used?: number;
  licenses_included?: number | null;
};

type SubscriptionTeamPanelProps = {
  language: 'en' | 'es';
  gatewayBaseUrl?: string;
  accessToken?: string;
  /** Platform operator path uses subscription id; account admins use /account/team. */
  mode: 'account' | 'subscription';
  subscriptionId?: string;
  contactEmail?: string;
  onClose?: () => void;
  startInvite?: boolean;
};

const JOBS = [
  { id: 'company_admin', en: 'Subscription admin', es: 'Admin de suscripción' },
  { id: 'contributor', en: 'Contributor', es: 'Colaborador' },
  { id: 'viewer', en: 'Viewer', es: 'Lector' },
] as const;

const RECORD_ROLES = [
  { id: 'none', en: 'No access', es: 'Sin acceso' },
  { id: 'viewer', en: 'Viewer', es: 'Lector' },
  { id: 'contributor', en: 'Contributor', es: 'Colaborador' },
  { id: 'project_admin', en: 'Project admin', es: 'Admin del proyecto' },
] as const;

type RecordRole = (typeof RECORD_ROLES)[number]['id'];

const SEAT_ROLES = [
  { id: 'member', job: 'contributor', en: 'Member', es: 'Miembro' },
  { id: 'company_admin', job: 'company_admin', en: 'Subscription admin', es: 'Admin de suscripción' },
] as const;

function projectLabelParts(name: string): { title: string; scope: string } {
  const [title, scope] = String(name || '').split(' — ');
  return { title: (title || name).trim(), scope: String(scope || '').trim() };
}

type RecordKind = 'government' | 'legal' | 'campus' | 'ai' | 'website' | 'folder';

function recordKind(label: string): RecordKind {
  const value = label.toLowerCase();
  if (value.includes('government') || value.includes('gubern')) return 'government';
  if (value.includes('legal') || value.includes('entidad')) return 'legal';
  if (value.includes('campus') || value.includes('educ')) return 'campus';
  if (value.includes(' ai') || value.startsWith('ai') || value.includes('inteligencia')) return 'ai';
  if (value.includes('website') || value.includes('web') || value.includes('sitio')) return 'website';
  return 'folder';
}

function initialsFromEmail(email: string): string {
  const local = String(email || '').split('@')[0] || '';
  const parts = local.split(/[._+-]+/).filter(Boolean);
  const letters = `${parts[0]?.[0] || ''}${parts[1]?.[0] || ''}`;
  return (letters || local.slice(0, 2) || '?').toUpperCase();
}

const RecordIcon: React.FC<{ kind: RecordKind }> = ({ kind }) => {
  const common = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  if (kind === 'government') {
    return <svg {...common}><path d="M12 3 4 7v2h16V7L12 3Z" /><path d="M6 9v8M10 9v8M14 9v8M18 9v8M4 17h16v3H4v-3Z" /></svg>;
  }
  if (kind === 'legal') {
    return <svg {...common}><path d="M4 20h16" /><path d="M6 20V10l6-6 6 6v10" /><path d="M10 20v-5h4v5" /></svg>;
  }
  if (kind === 'campus') {
    return <svg {...common}><path d="M3 10 12 4l9 6" /><path d="M5 10v8h14v-8" /><path d="M12 14v4" /></svg>;
  }
  if (kind === 'ai') {
    return <svg {...common}><path d="M12 3v3M12 18v3M3 12h3M18 12h3" /><circle cx="12" cy="12" r="4" /><path d="m8.5 8.5 1.2 1.2M14.3 14.3l1.2 1.2M15.5 8.5l-1.2 1.2M9.7 14.3 8.5 15.5" /></svg>;
  }
  if (kind === 'website') {
    return <svg {...common}><circle cx="12" cy="12" r="8" /><path d="M4 12h16M12 4c2.4 2.6 3.6 5.3 3.6 8s-1.2 5.4-3.6 8c-2.4-2.6-3.6-5.3-3.6-8s1.2-5.4 3.6-8Z" /></svg>;
  }
  return <svg {...common}><path d="M4 7h6l2 2h8v10H4V7Z" /></svg>;
};

function defaultInviteNote(accountName: string, language: 'en' | 'es'): string {
  const who = accountName.trim() || (language === 'es' ? 'esta suscripción' : 'this subscription');
  if (language === 'es') {
    return `Hola. Te invitamos a ${who} en NextCompli. Abre el enlace de este correo para aceptar. Entra con Google o Microsoft. Responde si tienes preguntas.`;
  }
  return `Hi. You're invited to ${who} on NextCompli. Open the link in this email to accept. Sign in with Google or Microsoft. Reply if you have questions.`;
}

const copy = {
  en: {
    title: 'Team',
    help: '',
    stepSubscription: 'Choose a subscription.',
    stepProjects: 'Open its projects. A person can be limited to some of them.',
    stepInvite: 'Enter an email, choose the subscription role, then set a role on each record.',
    inviteHeading: 'Invite someone',
    inviteLead: 'The subscription role is the seat on this account. Each record below has its own role.',
    seatHint: 'Subscription admin can manage people and billing, and can open every record. A member only opens the records you set below.',
    addNote: 'Add a note',
    hideNote: 'Hide note',
    inviteRecords: 'Role on each record',
    inviteAllRecords: 'A subscription admin can open every record. Record roles are not used.',
    invitePickRecord: 'Give them a role on at least one record.',
    inviteAccess: 'Access',
    inviteMessage: 'Message',
    inviteMessageHint: 'This note goes in the invitation email. Edit it before you send.',
    inviteNeedEmail: 'Enter an email address.',
    noAccess: 'No access',
    licensesOf: '{used} of {limit}',
    licensesHint: 'A pending invite uses a seat.',
    licensesUnlimited: 'Unlimited people on this plan. Each person is one named login.',
    email: 'Email',
    job: 'Subscription role',
    invite: 'Send invite',
    resend: 'Resend invite',
    remove: 'Remove',
    empty: 'No people on this subscription yet.',
    emptyActive: 'No accepted members yet.',
    addUser: 'Add user',
    inviteTitle: 'Invite',
    inviteSubtitle: 'Invite someone to this subscription.',
    seatType: 'Type',
    inviteTo: 'Invite to',
    invitedCount: 'Invited: {used}/{limit}',
    emptyPending: 'No pending invitations.',
    loadError: 'Could not load people.',
    saved: 'Invite sent to',
    emailFailed: 'The invite was saved, but the email did not send.',
    where: 'In Gmail, open Updates if it is not in Primary, and also check Spam.',
    updated: 'Job updated.',
    pending: 'Invite pending',
    active: 'Active',
    close: 'Close',
    sending: 'Sending…',
    copyLink: 'Copy invite link',
    linkCopied: 'Invite link copied. Share it if the email is delayed.',
    linkCopyFailed: 'Could not copy the invite link. Select and copy it from below.',
    tabActive: 'Members',
    tabPending: 'Pending invites',
    colPerson: 'Person',
    colJob: 'Subscription role',
    colStatus: 'Status',
    colInviteSent: 'Invite sent',
    colInviteAccepted: 'Accepted',
    colLastAccess: 'Last accessed',
    colGroups: 'Linked groups',
    colProjects: 'Projects',
    colActions: 'Actions',
    never: 'Never',
    allProjects: 'All records',
    projectAccess: 'Project access',
    projectAccessFor: 'Project access for',
    projectRole: 'Role on selected projects',
    saveProjects: 'Save project access',
    projectsSaved: 'Project access updated.',
    projectsOnAccount: 'Projects on this account',
    noProjectsYet: 'No projects on this subscription yet. Add one in a compliance console, then assign people here.',
    clearProjectLimits: 'Clear limits (all projects)',
    sourceDirect: 'Direct',
    sourceGroup: 'From group',
    sourceInvite: 'Invite',
  },
  es: {
    title: 'Equipo',
    help: '',
    stepSubscription: 'Elija una suscripción.',
    stepProjects: 'Abra sus proyectos. Una persona puede quedar limitada a algunos.',
    stepInvite: 'Escriba un correo, elija el puesto de la suscripción y luego un puesto en cada registro.',
    inviteHeading: 'Invitar a alguien',
    inviteLead: 'El puesto de la suscripción es el asiento en esta cuenta. Cada registro de abajo tiene su propio puesto.',
    seatHint: 'El admin de suscripción administra personas y facturación, y abre todos los registros. Un miembro solo abre los registros que usted marque abajo.',
    addNote: 'Agregar una nota',
    hideNote: 'Ocultar nota',
    inviteRecords: 'Puesto en cada registro',
    inviteAllRecords: 'Un admin de suscripción abre todos los registros. No se usan puestos por registro.',
    invitePickRecord: 'Asigne un puesto en al menos un registro.',
    inviteAccess: 'Acceso',
    inviteMessage: 'Mensaje',
    inviteMessageHint: 'Esta nota va en el correo de invitación. Edítela antes de enviar.',
    inviteNeedEmail: 'Escriba un correo.',
    noAccess: 'Sin acceso',
    licensesOf: '{used} de {limit}',
    licensesHint: 'Una invitación pendiente usa un puesto.',
    licensesUnlimited: 'Personas ilimitadas en este plan. Cada persona es un acceso con nombre.',
    email: 'Correo',
    job: 'Puesto en la suscripción',
    invite: 'Enviar invitación',
    resend: 'Reenviar invitación',
    remove: 'Quitar',
    empty: 'Todavía no hay personas en esta suscripción.',
    emptyActive: 'Todavía no hay miembros aceptados.',
    addUser: 'Agregar usuario',
    inviteTitle: 'Invitar',
    inviteSubtitle: 'Invitar a alguien a esta suscripción.',
    seatType: 'Tipo',
    inviteTo: 'Invitar a',
    invitedCount: 'Invitados: {used}/{limit}',
    emptyPending: 'No hay invitaciones pendientes.',
    loadError: 'No se pudo cargar la lista.',
    saved: 'Invitación enviada a',
    emailFailed: 'La invitación quedó guardada, pero el correo no salió.',
    where: 'En Gmail, abra Actualizaciones si no está en Principal, y también Spam.',
    updated: 'Puesto actualizado.',
    pending: 'Invitación pendiente',
    active: 'Activo',
    close: 'Cerrar',
    sending: 'Enviando…',
    copyLink: 'Copiar enlace de invitación',
    linkCopied: 'Enlace copiado. Compártalo si el correo se demora.',
    linkCopyFailed: 'No se pudo copiar el enlace. Selecciónelo abajo y cópielo.',
    tabActive: 'Miembros',
    tabPending: 'Invitaciones pendientes',
    colPerson: 'Persona',
    colJob: 'Puesto en la suscripción',
    colStatus: 'Estado',
    colInviteSent: 'Invitación enviada',
    colInviteAccepted: 'Aceptada',
    colLastAccess: 'Último acceso',
    colGroups: 'Grupos vinculados',
    colProjects: 'Proyectos',
    colActions: 'Acciones',
    never: 'Nunca',
    allProjects: 'Todos los registros',
    projectAccess: 'Acceso por proyecto',
    projectAccessFor: 'Acceso por proyecto para',
    projectRole: 'Puesto en los proyectos elegidos',
    saveProjects: 'Guardar acceso por proyecto',
    projectsSaved: 'Acceso por proyecto actualizado.',
    projectsOnAccount: 'Proyectos de esta cuenta',
    noProjectsYet: 'Todavía no hay proyectos en esta suscripción. Añada uno en una consola de cumplimiento y luego asigne personas aquí.',
    clearProjectLimits: 'Quitar límites (todos los proyectos)',
    sourceDirect: 'Directo',
    sourceGroup: 'Desde grupo',
    sourceInvite: 'Invitación',
  },
};

type Feedback = {
  kind: 'notice' | 'error';
  text: string;
  inviteUrl?: string | null;
};

type MemberTab = 'active' | 'pending';

function formatWhen(value: string | null | undefined, language: 'en' | 'es', neverLabel: string): string {
  if (!value) {
    return neverLabel;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return new Intl.DateTimeFormat(language === 'es' ? 'es' : 'en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

const SubscriptionTeamPanel: React.FC<SubscriptionTeamPanelProps> = ({
  language,
  gatewayBaseUrl,
  accessToken,
  mode,
  subscriptionId,
  onClose,
  startInvite = false,
}) => {
  const text = copy[language];
  const apiBase = String(gatewayBaseUrl || '').replace(/\/$/, '');
  const [members, setMembers] = React.useState<TeamMember[]>([]);
  const [projects, setProjects] = React.useState<ProjectOption[]>([]);
  const [subscriptions, setSubscriptions] = React.useState<TeamSubscription[]>([]);
  const [activeCompanyId, setActiveCompanyId] = React.useState<number | null>(null);
  const activeCompanyIdRef = React.useRef<number | null>(null);
  const [accountName, setAccountName] = React.useState('');
  const [licensesUsed, setLicensesUsed] = React.useState<number | null>(null);
  const [licensesIncluded, setLicensesIncluded] = React.useState<number | null | undefined>(undefined);
  const [email, setEmail] = React.useState('');
  const [seat, setSeat] = React.useState<(typeof SEAT_ROLES)[number]['id']>('member');
  const [showNote, setShowNote] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [inviteOpen, setInviteOpen] = React.useState(startInvite);
  const createMenuRef = React.useRef<HTMLDivElement | null>(null);
  const [inviteNote, setInviteNote] = React.useState(() => defaultInviteNote('', language));
  const noteTouched = React.useRef(false);
  const [projectRoles, setProjectRoles] = React.useState<Record<string, RecordRole>>({});
  const projectsTouched = React.useRef(false);
  const [tab, setTab] = React.useState<MemberTab>('active');
  const [feedback, setFeedback] = React.useState<Feedback | null>(null);
  const [busyKey, setBusyKey] = React.useState<string | null>(null);
  const [editingProjectsFor, setEditingProjectsFor] = React.useState<string | null>(null);
  const [draftRoles, setDraftRoles] = React.useState<Record<string, RecordRole>>({});
  const feedbackRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!noteTouched.current) {
      setInviteNote(defaultInviteNote(accountName, language));
    }
  }, [accountName, language]);

  React.useEffect(() => {
    if (!createOpen && !inviteOpen) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      if (inviteOpen) {
        setInviteOpen(false);
        return;
      }
      setCreateOpen(false);
    };
    const onPointer = (event: MouseEvent) => {
      if (!createOpen || inviteOpen) {
        return;
      }
      if (!createMenuRef.current?.contains(event.target as Node)) {
        setCreateOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [createOpen, inviteOpen]);

  const basePath = mode === 'subscription'
    ? `/api/v1/admin/paying-subscriptions/${encodeURIComponent(String(subscriptionId || ''))}/members`
    : '/api/v1/account/team';
  const withCompany = React.useCallback((path: string) => {
    if (mode !== 'account' || !activeCompanyIdRef.current) {
      return path;
    }
    const joiner = path.includes('?') ? '&' : '?';
    return `${path}${joiner}company_id=${activeCompanyIdRef.current}`;
  }, [mode]);

  const load = React.useCallback(async () => {
    if (!apiBase || !accessToken) {
      return;
    }
    if (mode === 'subscription' && !subscriptionId) {
      return;
    }
    const path = mode === 'subscription' ? basePath : '/api/v1/account/team';
    const response = await fetch(`${apiBase}${path}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body?.detail || text.loadError);
    }
    const rows: TeamSubscription[] = Array.isArray(body.subscriptions)
      ? body.subscriptions
      : [{
        company_id: Number(body.company_id || 0),
        company_name: String(body.company_name || ''),
        members: Array.isArray(body.members) ? body.members : [],
        projects: Array.isArray(body.projects) ? body.projects : [],
        licenses_used: Number(body.licenses_used || 0),
        licenses_included: body.licenses_included,
      }];
    setSubscriptions(rows);
    const preferred = activeCompanyIdRef.current;
    const selected = rows.find((row) => row.company_id === preferred) || rows[0];
    if (selected) {
      activeCompanyIdRef.current = selected.company_id;
      setActiveCompanyId(selected.company_id);
      setMembers(Array.isArray(selected.members) ? selected.members : []);
      setProjects(Array.isArray(selected.projects) ? selected.projects : []);
      setAccountName(String(selected.company_name || ''));
      if (Object.prototype.hasOwnProperty.call(selected, 'licenses_included')) {
        const included = selected.licenses_included;
        setLicensesIncluded(included == null ? null : Number(included));
        setLicensesUsed(Number(selected.licenses_used || 0));
      }
    }
  }, [accessToken, apiBase, basePath, mode, subscriptionId, text.loadError]);

  const projectKey = projects.map((project) => project.project_id).join('|');
  React.useEffect(() => {
    if (projectsTouched.current) {
      return;
    }
    setProjectRoles(Object.fromEntries(projects.map((project) => [project.project_id, 'contributor'])));
  }, [projectKey, projects]);

  React.useEffect(() => {
    let cancelled = false;
    void load().catch((loadError: unknown) => {
      if (!cancelled) {
        setFeedback({
          kind: 'error',
          text: loadError instanceof Error ? loadError.message : text.loadError,
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [load, text.loadError]);

  React.useEffect(() => {
    if (feedback && feedbackRef.current) {
      scrollElementWithinConsole(feedbackRef.current);
    }
  }, [feedback]);

  const unwrapInviteBody = (body: Record<string, unknown>) => {
    const nested = body?.member;
    if (nested && typeof nested === 'object') {
      return nested as Record<string, unknown>;
    }
    return body;
  };

  const applyDeliveryResult = async (mailbox: string, body: Record<string, unknown>) => {
    const payload = unwrapInviteBody(body);
    const delivery = String((payload?.invite_delivery as { delivery?: string } | undefined)?.delivery || '');
    const inviteUrl = String((payload?.invite as { invite_url?: string } | undefined)?.invite_url || '').trim() || null;
    const deliveryError = String(
      (payload?.invite_delivery as { error?: string } | undefined)?.error || '',
    ).trim();
    if (delivery !== 'sent') {
      setFeedback({
        kind: 'error',
        text: deliveryError || `${text.emailFailed} ${mailbox}`.trim(),
        inviteUrl,
      });
      return;
    }
    setFeedback({
      kind: 'notice',
      text: `${text.saved} ${mailbox.trim()}. ${text.where}`,
      inviteUrl: null,
    });
  };

  const inviteEmail = async (
    mailbox: string,
    nextJob: string,
    busy: string,
    opts?: { resendUserId?: string; projectGrants?: Array<{ project_id: string; role: string }>; message?: string },
  ) => {
    if (!apiBase || !accessToken) {
      return;
    }
    const trimmed = mailbox.trim();
    if (!trimmed) {
      return;
    }
    setBusyKey(busy);
    setFeedback(null);
    try {
      if (opts?.resendUserId) {
        const path = mode === 'subscription'
          ? `${basePath}/${encodeURIComponent(opts.resendUserId)}/resend-invite`
          : `/api/v1/account/team/${encodeURIComponent(opts.resendUserId)}/resend-invite`;
        const response = await fetch(`${apiBase}${withCompany(path)}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ source: busy, message: opts?.message || '' }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          setFeedback({ kind: 'error', text: body?.detail || text.loadError });
          return;
        }
        await applyDeliveryResult(trimmed, body);
        await load();
        return;
      }

      const path = mode === 'subscription' ? basePath : '/api/v1/account/team/invite';
      const response = await fetch(`${apiBase}${withCompany(path)}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: trimmed,
          job: nextJob,
          source: busy,
          project_grants: opts?.projectGrants || [],
          message: opts?.message || '',
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setFeedback({ kind: 'error', text: body?.detail || text.loadError });
        return;
      }
      await applyDeliveryResult(trimmed, body);
      setEmail('');
      setSeat('member');
      noteTouched.current = false;
      setShowNote(false);
      setInviteOpen(false);
      setInviteNote(defaultInviteNote(accountName, language));
      projectsTouched.current = false;
      setProjectRoles(Object.fromEntries(projects.map((project) => [project.project_id, 'contributor'])));
      setTab('pending');
      await load();
    } finally {
      setBusyKey(null);
    }
  };

  const invite = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim()) {
      setFeedback({ kind: 'error', text: text.inviteNeedEmail });
      return;
    }
    const existing = members.find((member) => member.email.toLowerCase() === email.trim().toLowerCase());
    const pending = existing && String(existing.invite_status || '').toLowerCase() === 'pending';
    const seatRole = SEAT_ROLES.find((entry) => entry.id === seat) || SEAT_ROLES[0];
    const grants = seatRole.id === 'company_admin'
      ? []
      : projects
        .map((project) => ({
          project_id: project.project_id,
          role: projectRoles[project.project_id] || 'contributor',
        }))
        .filter((grant) => grant.role !== 'none');
    if (seatRole.id !== 'company_admin' && projects.length > 0 && grants.length === 0) {
      setFeedback({ kind: 'error', text: text.invitePickRecord });
      return;
    }
    await inviteEmail(email, seatRole.job, 'form', {
      resendUserId: pending ? existing?.user_id : undefined,
      projectGrants: grants,
      message: inviteNote.trim(),
    });
  };

  const changeJob = async (userId: string, nextJob: string) => {
    if (!apiBase || !accessToken) {
      return;
    }
    const path = mode === 'subscription'
      ? `${basePath}/${encodeURIComponent(userId)}`
      : `/api/v1/account/team/${encodeURIComponent(userId)}`;
    const response = await fetch(`${apiBase}${withCompany(path)}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ job: nextJob }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setFeedback({ kind: 'error', text: body?.detail || text.loadError });
      return;
    }
    setFeedback({ kind: 'notice', text: text.updated });
    await load();
  };

  const remove = async (userId: string) => {
    if (!apiBase || !accessToken) {
      return;
    }
    const path = mode === 'subscription'
      ? `${basePath}/${encodeURIComponent(userId)}`
      : `/api/v1/account/team/${encodeURIComponent(userId)}`;
    const response = await fetch(`${apiBase}${withCompany(path)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setFeedback({ kind: 'error', text: body?.detail || text.loadError });
      return;
    }
    setFeedback(null);
    await load();
  };

  const grantsFromRoles = (roles: Record<string, RecordRole>) => (
    Object.entries(roles)
      .filter(([, role]) => role !== 'none')
      .map(([project_id, role]) => ({ project_id, role }))
  );

  const saveProjectAccess = async (
    userId: string,
    roles: Record<string, RecordRole> = draftRoles,
  ) => {
    if (!apiBase || !accessToken) {
      return;
    }
    setBusyKey(`projects:${userId}`);
    try {
      const path = mode === 'subscription'
        ? `${basePath}/${encodeURIComponent(userId)}/projects`
        : `/api/v1/account/team/${encodeURIComponent(userId)}/projects`;
      const response = await fetch(`${apiBase}${withCompany(path)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_grants: grantsFromRoles(roles),
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setFeedback({ kind: 'error', text: body?.detail || text.loadError });
        return;
      }
      setFeedback({ kind: 'notice', text: text.projectsSaved });
      setEditingProjectsFor(null);
      await load();
    } finally {
      setBusyKey(null);
    }
  };

  const copyInviteLink = async () => {
    const link = feedback?.inviteUrl;
    if (!link) {
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setFeedback({
        kind: 'notice',
        text: text.linkCopied,
        inviteUrl: link,
      });
    } catch {
      setFeedback({
        kind: 'error',
        text: text.linkCopyFailed,
        inviteUrl: link,
      });
    }
  };

  const pendingMembers = members.filter((member) => (
    ['pending', 'expired'].includes(String(member.invite_status || '').toLowerCase())
  ));
  const activeMembers = members.filter((member) => (
    !['pending', 'expired', 'revoked', 'cancelled', 'canceled'].includes(
      String(member.invite_status || '').toLowerCase(),
    )
  ));
  const visibleMembers = tab === 'pending' ? pendingMembers : activeMembers;

  const formInviteLabel = (() => {
    const existing = members.find((member) => member.email.toLowerCase() === email.trim().toLowerCase());
    if (existing && String(existing.invite_status || '').toLowerCase() === 'pending') {
      return text.resend;
    }
    return text.invite;
  })();

  const editingMember = editingProjectsFor
    ? members.find((member) => member.user_id === editingProjectsFor) || null
    : null;

  return (
    <div className="sub-catalog__editor" aria-label={text.title}>
      {onClose ? null : (
      <div className="sub-catalog__card-top">
        <div>
          <h3>{text.title}{accountName ? ` — ${accountName}` : ''}</h3>
          {subscriptions.length > 0 ? (
            <div className="team-subscription-list" role="listbox" aria-label={text.stepSubscription}>
              {subscriptions.map((row) => (
                <button
                  key={row.company_id}
                  type="button"
                  role="option"
                  aria-selected={row.company_id === activeCompanyId}
                  className={`team-subscription-pick${row.company_id === activeCompanyId ? ' is-selected' : ''}`}
                  onClick={() => {
                    activeCompanyIdRef.current = row.company_id;
                    setActiveCompanyId(row.company_id);
                    setMembers(Array.isArray(row.members) ? row.members : []);
                    projectsTouched.current = false;
                    setProjects(Array.isArray(row.projects) ? row.projects : []);
                    setAccountName(String(row.company_name || ''));
                    const included = row.licenses_included;
                    setLicensesIncluded(included == null ? null : Number(included));
                    setLicensesUsed(Number(row.licenses_used || 0));
                  }}
                >
                  {row.company_name || text.title}
                </button>
              ))}
            </div>
          ) : null}
          {projects.length ? (
            <ul className="team-record-row">
              {projects.map((project) => {
                const parts = projectLabelParts(project.project_name);
                const kind = recordKind(`${parts.scope} ${parts.title}`);
                return (
                  <li key={project.project_id} className={`team-record team-record--${kind}`}>
                    <RecordIcon kind={kind} />
                    <span>
                      <strong>{parts.title}</strong>
                      {parts.scope ? <small>{parts.scope}</small> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="sub-catalog__meta">{text.noProjectsYet}</p>
          )}
        </div>
      </div>
      )}

      <div className="sub-catalog__member-board">
        <div className="sub-catalog__member-toolbar">
        <div className="sub-catalog__member-tabs" role="tablist" aria-label={text.title}>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'active'}
            className={`sub-catalog__member-tab${tab === 'active' ? ' is-active' : ''}`}
            onClick={() => setTab('active')}
          >
            {text.tabActive} ({activeMembers.length})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'pending'}
            className={`sub-catalog__member-tab${tab === 'pending' ? ' is-active' : ''}`}
            onClick={() => setTab('pending')}
          >
            {text.tabPending} ({pendingMembers.length})
          </button>
        </div>
        {typeof licensesIncluded === 'number' ? (
          <span
            className="team-seat-pill"
            aria-label={`${text.licensesOf.replace('{used}', String(licensesUsed ?? 0)).replace('{limit}', String(licensesIncluded))}. ${text.licensesHint}`}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <circle cx="12" cy="8" r="3" />
              <path d="M5 19c1.2-3 3.6-4.5 7-4.5S17.8 16 19 19" />
            </svg>
            {text.licensesOf.replace('{used}', String(licensesUsed ?? 0)).replace('{limit}', String(licensesIncluded))}
          </span>
        ) : licensesIncluded === null ? (
          <span className="team-seat-pill">{text.licensesUnlimited}</span>
        ) : null}
        <div className="team-create" ref={createMenuRef}>
          <button
            type="button"
            className="team-create__plus"
            aria-expanded={createOpen}
            aria-haspopup="menu"
            aria-label={text.addUser}
            onClick={() => setCreateOpen((open) => !open)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="9" cy="8" r="3" />
              <path d="M3.5 19c.8-2.6 2.8-4 5.5-4s4.7 1.4 5.5 4" />
              <path d="M19 8v6M16 11h6" />
            </svg>
          </button>
          {createOpen ? (
            <div className="team-create__menu" role="menu">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setCreateOpen(false);
                  setInviteOpen(true);
                }}
              >
                {text.addUser}
              </button>
            </div>
          ) : null}
          {onClose ? (
            <button type="button" className="btn btn-secondary" onClick={onClose}>{text.close}</button>
          ) : null}
        </div>
        </div>
        {visibleMembers.length === 0 ? (
          <p className="sub-catalog__meta">{tab === 'pending' ? text.emptyPending : text.emptyActive}</p>
        ) : (
          <ul className="team-member-list">
            {visibleMembers.map((member) => {
              const pending = String(member.invite_status || '').toLowerCase() === 'pending'
                || String(member.invite_status || '').toLowerCase() === 'expired';
              const groups = member.linked_groups || [];
              const access = member.project_access || [];
              return (
                <li key={member.user_id} className="team-member-card">
                  <span className="team-member-card__avatar" aria-hidden="true">{initialsFromEmail(member.email)}</span>
                  <div className="team-member-card__id">
                    <span className="team-member-card__email">{member.email}</span>
                    <span className={`team-member-card__status${pending ? ' is-pending' : ' is-active'}`}>
                      {pending ? text.pending : text.active}
                    </span>
                  </div>
                  <div className="team-member-card__meta">
                    <label className="team-member-card__role">
                      <span className="visually-hidden">{text.job}</span>
                      <select
                        aria-label={`${text.job}: ${member.email}`}
                        value={member.job || 'contributor'}
                        onChange={(event) => void changeJob(member.user_id, event.target.value)}
                        disabled={Boolean(busyKey)}
                      >
                        {JOBS.map((entry) => (
                          <option key={entry.id} value={entry.id}>{entry[language]}</option>
                        ))}
                      </select>
                    </label>
                    {member.membership_source === 'group' ? (
                      <span className="team-fact">{text.sourceGroup}{groups.length ? ` · ${groups.map((group) => group.name).join(', ')}` : ''}</span>
                    ) : null}
                    <span className="team-fact">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="12" r="8" /><path d="M12 8v5l3 2" /></svg>
                      {text.colLastAccess}: {formatWhen(member.last_login_at, language, text.never)}
                    </span>
                    <span className="team-fact">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M5 12.5 9.2 17 19 7" /></svg>
                      {pending ? text.colInviteSent : text.colInviteAccepted}: {formatWhen(pending ? (member.invite_last_sent_at || member.invite_created_at) : member.invite_accepted_at, language, text.never)}
                    </span>
                    {access.length ? access.map((item) => {
                      const kind = recordKind(String(item.project_name || ''));
                      return (
                        <span key={`${item.project_id}:${item.role}`} className={`team-fact team-fact--${kind}`}>
                          <RecordIcon kind={kind} />
                          {item.project_name || item.project_id}
                        </span>
                      );
                    }) : (
                      <span className="team-fact">
                        <RecordIcon kind="folder" />
                        {text.allProjects}
                      </span>
                    )}
                  </div>
                  <div className="team-member-card__actions">
                    {pending ? (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={Boolean(busyKey)}
                        onClick={() => void inviteEmail(member.email, member.job || 'contributor', 'row', {
                          resendUserId: member.user_id,
                        })}
                      >
                        {busyKey === 'row' ? text.sending : text.resend}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="team-icon-btn"
                      disabled={Boolean(busyKey)}
                      onClick={() => {
                        const assigned = new Map(
                          (member.project_access || []).map((item) => [item.project_id, item.role]),
                        );
                        setEditingProjectsFor(member.user_id);
                        setDraftRoles(Object.fromEntries(projects.map((project) => [
                          project.project_id,
                          (assigned.get(project.project_id) as RecordRole) || 'none',
                        ])));
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M4 7h6l2 2h8v10H4V7Z" /></svg>
                      {text.projectAccess}
                    </button>
                    <button
                      type="button"
                      className="team-icon-btn team-icon-btn--danger"
                      disabled={Boolean(busyKey)}
                      onClick={() => void remove(member.user_id)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M5 7h14M9 7V5h6v2M8 7l1 13h6l1-13" /></svg>
                      {text.remove}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div ref={feedbackRef} className="sub-catalog__team-feedback">
        {feedback ? (
          <p
            className={feedback.kind === 'error' ? 'sub-catalog__error' : 'sub-catalog__notice'}
            role={feedback.kind === 'error' ? 'alert' : 'status'}
          >
            {feedback.text}
          </p>
        ) : null}
        {feedback?.inviteUrl ? (
          <div className="sub-catalog__invite-link">
            <code>{feedback.inviteUrl}</code>
            <button type="button" className="btn btn-secondary" onClick={() => void copyInviteLink()}>
              {text.copyLink}
            </button>
          </div>
        ) : null}
      </div>

      {editingMember ? (
        <div
          className="sub-catalog__project-drawer"
          role="dialog"
          aria-modal="false"
          aria-label={`${text.projectAccessFor} ${editingMember.email}`}
        >
          <div className="sub-catalog__card-top">
            <div>
              <h4>{text.projectAccessFor} {editingMember.email}</h4>
              <p className="sub-catalog__meta">
                {projects.length
                  ? text.projectsOnAccount
                  : text.noProjectsYet}
              </p>
            </div>
            <button type="button" className="btn btn-secondary" onClick={() => setEditingProjectsFor(null)}>
              {text.close}
            </button>
          </div>
          {projects.length ? (
            <ul className="sub-catalog__project-picker">
              {projects.map((project) => {
                const role = draftRoles[project.project_id] || 'none';
                const parts = projectLabelParts(project.project_name);
                return (
                  <li key={project.project_id}>
                    <div className={`sub-catalog__project-option${role !== 'none' ? ' is-selected' : ''}`}>
                      <span className={`team-record team-record--${recordKind(`${parts.scope} ${parts.title}`)}`}>
                        <RecordIcon kind={recordKind(`${parts.scope} ${parts.title}`)} />
                        <span>
                          {parts.title}
                          {parts.scope ? <small className="sub-catalog__project-scope">{parts.scope}</small> : null}
                        </span>
                      </span>
                      <select
                        aria-label={`${parts.title} ${text.job}`}
                        value={role}
                        onChange={(event) => setDraftRoles((current) => ({
                          ...current,
                          [project.project_id]: event.target.value as RecordRole,
                        }))}
                      >
                        {RECORD_ROLES.map((entry) => (
                          <option key={entry.id} value={entry.id}>{entry[language]}</option>
                        ))}
                      </select>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}
          <div className="sub-catalog__member-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={Boolean(busyKey) || projects.length === 0}
              onClick={() => void saveProjectAccess(editingMember.user_id)}
            >
              {busyKey === `projects:${editingMember.user_id}` ? text.sending : text.saveProjects}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={Boolean(busyKey)}
              onClick={() => {
                const cleared = Object.fromEntries(projects.map((project) => [project.project_id, 'none'])) as Record<string, RecordRole>;
                setDraftRoles(cleared);
                void saveProjectAccess(editingMember.user_id, cleared);
              }}
            >
              {text.clearProjectLimits}
            </button>
          </div>
        </div>
      ) : null}

      {inviteOpen ? (
        <div className="team-invite-backdrop" onClick={() => setInviteOpen(false)}>
          <form
            className="team-invite-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-invite-title"
            onClick={(event) => event.stopPropagation()}
            onSubmit={(event) => void invite(event)}
          >
            <div className="team-invite-dialog__head">
              <h4 id="team-invite-title">{text.inviteTitle}</h4>
              <button type="button" aria-label={text.close} onClick={() => setInviteOpen(false)}>×</button>
            </div>
            <p className="team-invite-dialog__lead">{text.inviteSubtitle}</p>
            {feedback?.kind === 'error' ? (
              <p className="sub-catalog__error" role="alert">{feedback.text}</p>
            ) : null}
            <label className="team-invite-dialog__email">
              <span>{text.email}</span>
              <input
                aria-label={text.email}
                type="email"
                value={email}
                autoFocus
                onChange={(event) => setEmail(event.target.value)}
                disabled={Boolean(busyKey)}
              />
            </label>
            <p className="team-invite-dialog__kicker">{text.seatType}</p>
            <fieldset className="team-invite-dialog__seats">
              <legend className="visually-hidden">{text.job}</legend>
              {SEAT_ROLES.map((entry) => (
                <label key={entry.id}>
                  <input
                    type="radio"
                    name="subscription-seat"
                    value={entry.id}
                    checked={seat === entry.id}
                    disabled={Boolean(busyKey)}
                    onChange={() => setSeat(entry.id)}
                  />
                  <span>{entry[language]}</span>
                  {entry.id === 'member' && typeof licensesIncluded === 'number' ? (
                    <small>
                      {text.invitedCount
                        .replace('{used}', String(licensesUsed ?? 0))
                        .replace('{limit}', String(licensesIncluded))}
                    </small>
                  ) : null}
                </label>
              ))}
            </fieldset>
            {seat === 'company_admin' ? (
              <p className="team-invite__hint">{text.inviteAllRecords}</p>
            ) : projects.length ? (
              <div className="team-invite-dialog__records">
                <p className="team-invite-dialog__kicker">{text.inviteTo}</p>
                <ul aria-label={text.inviteRecords}>
                  {projects.map((project) => {
                    const role = projectRoles[project.project_id] || 'contributor';
                    const parts = projectLabelParts(project.project_name);
                    return (
                      <li key={project.project_id}>
                        <span className={`team-record team-record--${recordKind(`${parts.scope} ${parts.title}`)}`}>
                          <RecordIcon kind={recordKind(`${parts.scope} ${parts.title}`)} />
                          <span>
                            {parts.title}
                            {parts.scope ? <small>{parts.scope}</small> : null}
                          </span>
                        </span>
                        <select
                          aria-label={`${parts.title} ${text.inviteRecords}`}
                          value={role}
                          disabled={Boolean(busyKey)}
                          onChange={(event) => {
                            projectsTouched.current = true;
                            setProjectRoles((current) => ({
                              ...current,
                              [project.project_id]: event.target.value as RecordRole,
                            }));
                          }}
                        >
                          {RECORD_ROLES.map((entry) => (
                            <option key={entry.id} value={entry.id}>{entry[language]}</option>
                          ))}
                        </select>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            <button
              type="button"
              className="team-invite__note-toggle"
              aria-expanded={showNote}
              onClick={() => setShowNote((open) => !open)}
            >
              {showNote ? text.hideNote : text.addNote}
            </button>
            {showNote ? (
              <label className="team-invite__note">
                <span className="visually-hidden">{text.inviteMessage}</span>
                <textarea
                  aria-label={text.inviteMessage}
                  rows={3}
                  value={inviteNote}
                  maxLength={800}
                  disabled={Boolean(busyKey)}
                  onChange={(event) => {
                    noteTouched.current = true;
                    setInviteNote(event.target.value);
                  }}
                />
              </label>
            ) : null}
            <div className="team-invite-dialog__actions">
              <button type="button" className="btn btn-secondary" onClick={() => setInviteOpen(false)}>
                {text.close}
              </button>
              <button type="submit" className="btn btn-primary" disabled={Boolean(busyKey)}>
                {busyKey === 'form' ? text.sending : (formInviteLabel === text.resend ? text.resend : text.inviteTitle)}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default SubscriptionTeamPanel;
